import React, { useMemo, useState } from "react";
import "../../css/items.scss";
import { formatAmount, formatPricingDate, getPricingPreview, getTierLabel } from "../meetings/meetingPricing";
import { serverInstance } from "../../service/axios";
import "../meetings/MeetingPayment.scss";

const EventPayment = ({ email, resident, eventId, eventInfo, membershipActive, language, capacityFullMessage }) => {
  const [qpayInvoice, setQpayInvoice] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [gettingInvoice, setGettingInvoice] = useState(false);
  const [checking, setChecking] = useState(false);
  const [paid, setPaid] = useState(false);
  const [capacityFull, setCapacityFull] = useState(false);

  const pricingPreview = useMemo(() => getPricingPreview({ meetingInfo: eventInfo, resident, membershipActive, allowDiscountOnly: true }), [eventInfo, resident, membershipActive]);
  const pricingSummary = pricingInfo || pricingPreview;
  const eventInvoiceStorageKey = `event_invoice_id_${eventId || "unknown"}_${pricingPreview?.tier || "unknown"}_${pricingPreview?.amount ?? "unknown"}`;

  const normalizeInvoicePricing = (pricing) => pricing ? {
    ...pricing,
    tier: pricing.tier || pricing.pricing_tier || pricingPreview?.tier,
    amount: pricing.amount,
    currency: pricing.currency || "MNT",
    discount_ends_at: pricing.discount_ends_at || pricing.event_discount_ends_at || pricing.meeting_discount_ends_at || pricingPreview?.discount_ends_at || "",
  } : null;

  const invoiceMatchesPreview = (invoicePricing) => {
    if (!pricingPreview || !invoicePricing) {
      return true;
    }

    if (invoicePricing.tier && invoicePricing.tier !== pricingPreview.tier) {
      return false;
    }

    return !pricingPreview.amount || Number(invoicePricing.amount) === Number(pricingPreview.amount);
  };

  const handleInvoice = () => {
    if (capacityFull) {
      setErrorMsg(capacityFullMessage || (language
        ? "Registration is closed. This event has reached its capacity."
        : "Бүртгэл хаагдсан. Энэ эвентийн багтаамж дүүрсэн байна."));
      return;
    }

    if (!email || !eventId) {
      setErrorMsg(language ? "Event registration is incomplete" : "Эвентийн бүртгэлийн мэдээлэл дутуу байна");
      return;
    }

    if (qpayInvoice?.qr_image || gettingInvoice) {
      return;
    }

    const token = localStorage.getItem("msidclienttoken");
    const storedInvoiceId = localStorage.getItem(eventInvoiceStorageKey);
    if (storedInvoiceId && token) {
      getExistingInvoice(storedInvoiceId);
    } else {
      createNewInvoice();
    }
  };

  const createNewInvoice = () => {
    setGettingInvoice(true);
    serverInstance.post("/client/invoice/event", {
      email: email,
      resident: resident,
      event_id: eventId,
      pricing_tier: pricingPreview?.tier,
      pricing_preview: pricingPreview,
    }, {
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("msidclienttoken")}`,
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        const invoicePricing = normalizeInvoicePricing(res.data.pricing || (res.data.amount !== undefined ? {
          amount: res.data.amount,
          currency: "MNT",
          tier: res.data.pricing_tier || pricingPreview?.tier,
        } : null));
        if (!invoiceMatchesPreview(invoicePricing)) {
          localStorage.removeItem(eventInvoiceStorageKey);
          setInvoiceId(null);
          setQpayInvoice(null);
          setPricingInfo(null);
          setErrorMsg(language
            ? "Invoice price does not match the selected attendee type. Please try again."
            : "Нэхэмжлэхийн үнэ сонгосон төрөлтэй тохирохгүй байна. Дахин оролдоно уу.");
          return;
        }
        setInvoiceId(res.data.invoice_id);
        setQpayInvoice(res.data.qpay_invoice);
        setPricingInfo(invoicePricing);
        setErrorMsg("");
        localStorage.setItem(eventInvoiceStorageKey, res.data.invoice_id);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setErrorMsg(language ? "Please sign in again and try again" : "Та дахин нэвтрээд оролдоно уу!");
        } else if (err?.response?.status === 409 && err?.response?.data?.capacity_full) {
          setCapacityFull(true);
          setErrorMsg(capacityFullMessage || (language
            ? "Registration is closed. This event has reached its capacity."
            : "Бүртгэл хаагдсан. Энэ эвентийн багтаамж дүүрсэн байна."));
        } else {
          const message = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err.response.data : "");
          setErrorMsg(message || (language ? "Could not create payment invoice" : "Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа!"));
        }
      })
      .finally(() => setGettingInvoice(false));
  };

  const getExistingInvoice = (storedInvoiceId) => {
    setGettingInvoice(true);
    serverInstance.get(`/client/invoice?invoiceId=${storedInvoiceId}`, {
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("msidclienttoken")}`,
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        if (res.status === 200) {
          const qpayInvoice = JSON.parse(res.data.invoice.text_value);
          const invoicePricing = normalizeInvoicePricing(res.data.invoice.pricing_snapshot || {
            amount: res.data.invoice.amount,
            currency: res.data.invoice.currency || "MNT",
            original_amount: res.data.invoice.original_amount,
            discount_amount: res.data.invoice.discount_amount,
            discount_code: res.data.invoice.discount_code,
            discount_ends_at: res.data.invoice.discount_ends_at,
            tier: res.data.invoice.pricing_tier,
          });
          const sameEventInvoice = res.data.invoice.product === "event"
            && String(res.data.invoice.product_id) === String(eventId)
            && res.data.invoice.email === email
            && (!pricingPreview?.tier || !invoicePricing?.tier || invoicePricing.tier === pricingPreview.tier)
            && (!pricingPreview?.amount || Number(invoicePricing.amount) === Number(pricingPreview.amount));

          if (sameEventInvoice) {
            setInvoiceId(res.data.invoice.id);
            setQpayInvoice(qpayInvoice);
            setPricingInfo(invoicePricing);
            setErrorMsg("");
          } else {
            localStorage.removeItem(eventInvoiceStorageKey);
            createNewInvoice();
          }
        } else {
          setSuccessMsg("");
          setErrorMsg((language ? "Could not create payment invoice. Code: " : "Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа. Код: ") + res.status);
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 404 || err?.status === 404) {
          localStorage.removeItem(eventInvoiceStorageKey);
          createNewInvoice();
        } else {
          setErrorMsg((language ? "Could not create payment invoice" : "Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа!") + ` ${err?.response?.status || ""}`);
        }
      })
      .finally(() => setGettingInvoice(false));
  };

  const checkInvoice = () => {
    setChecking(true);
    serverInstance.post("/client/invoice/check", {
      invoice_id: invoiceId
    }, {
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("msidclienttoken")}`,
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        if (res.data.paid === true) {
          setSuccessMsg(language ? "Payment completed successfully" : "Төлбөр амжилттай төлөгдсөн байна");
          setErrorMsg("");
          setPaid(true);
          localStorage.removeItem(eventInvoiceStorageKey);
        } else {
          setSuccessMsg("");
          setErrorMsg(language ? "Payment is not completed" : "Төлбөр төлөгдөөгүй байна!");
          setPaid(false);
        }
      })
      .catch((err) => {
        setErrorMsg(err?.response?.data?.message || (language ? "Could not check payment" : "Төлбөр шалгахад алдаа гарлаа"));
      })
      .finally(() => setChecking(false));
  };

  const earlyBirdEndDate = formatPricingDate(
    pricingSummary?.event_discount_ends_at || pricingSummary?.meeting_discount_ends_at || pricingSummary?.discount_ends_at || pricingPreview?.discount_ends_at,
    language
  );

  return (
    <div className="meeting-payment">
      {errorMsg ? <div className="error-msg">{errorMsg}</div> : null}
      {successMsg ? <div className="success-msg">{successMsg}</div> : null}
      {!paid && pricingSummary ? (
        <div className="pricing-summary">
          <div className="pricing-line">
            <span>{language ? "Registration fee" : "Бүртгэлийн төлбөр"}</span>
            <strong>{formatAmount(pricingSummary.amount, pricingSummary.currency)}</strong>
          </div>
          {pricingSummary.tier ? (
            <div className="pricing-meta">
              {language ? "Type" : "Төрөл"}: {getTierLabel(pricingSummary.tier, language)}
            </div>
          ) : null}
          {Number(pricingSummary.discount_amount) > 0 ? (
            <div className="pricing-discount">
              <span>
                {pricingSummary.event_discount_label || pricingSummary.meeting_discount_label || pricingSummary.discount_label || (language ? "Early bird discount" : "Early bird хөнгөлөлт")}: -{formatAmount(pricingSummary.event_discount_amount ?? pricingSummary.meeting_discount_amount ?? pricingSummary.discount_amount, pricingSummary.currency)}
              </span>
              {earlyBirdEndDate ? (
                <span className="pricing-discount-end-date">
                  {language ? "Ends" : "Дуусах"}: {earlyBirdEndDate}
                </span>
              ) : null}
            </div>
          ) : null}
          {Number(pricingSummary.original_amount) > Number(pricingSummary.amount) ? (
            <div className="pricing-meta">
              {language ? "Original fee" : "Үндсэн үнэ"}: {formatAmount(pricingSummary.original_amount, pricingSummary.currency)}
            </div>
          ) : null}
        </div>
      ) : null}

      {paid ? null : capacityFull ? null : (
        <>
          {qpayInvoice?.qr_image ? (
            <img
              className="qr-code"
              src={`data:image/png;base64,${qpayInvoice?.qr_image}`}
              alt="QR Code"
              style={{ width: "300px", height: "300px" }}
            />
          ) : (
            <div className="get-invoice">
              <button className="get" disabled={gettingInvoice || capacityFull} onClick={handleInvoice}>
                {gettingInvoice ? (language ? "Please wait..." : "Түр хүлээнэ үү...") : "Qpay-р төлөх"}
              </button>
            </div>
          )}

          {invoiceId ? (
            <div className="check-payment">
              <button className="check" disabled={checking || capacityFull} onClick={checkInvoice}>
                {checking ? (language ? "Checking..." : "Шалгаж байна...") : (language ? "Check payment" : "Төлбөр шалгах")}
              </button>
            </div>
          ) : null}

          {paid || !qpayInvoice?.qr_image || capacityFull ? null : (
            <div className="mobile-only">
              <div className="title">{language ? "Pay with mobile app:" : "Гар утасны аппаар төлөх:"}</div>
              <div className="payment-apps">
                {qpayInvoice?.urls.map((item, index) => (
                  <div key={index} className="payment-app">
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      <img className="logo" src={item.logo} alt={item.description || ""} />
                      <div className="name">{item.description}</div>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventPayment;
